import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, CheckCircle2, Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import { services, navigation, mainCategories, contactServices } from './data';
import Malerservice from './pages/Malerservice';
import About from './pages/About';

const FadeInSection = ({ children }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    const { current } = domRef;
    observer.observe(current);

    return () => observer.unobserve(current);
  }, []);

  return (
    <div
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      ref={domRef}
    >
      {children}
    </div>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <nav className="bg-[#ecfdf5] border-b border-gray-100 sticky top-0 z-50">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 md:h-28 items-center">
          <Link to="/" className="flex items-center">
            <img 
              src="/Kundebilleder/alss_logo.png" 
              alt="Alssund Rengøring & Malerservice" 
              className="h-16 md:h-24 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                  location.pathname === item.href ? 'text-emerald-600' : 'text-gray-500'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/kontakt"
              className="bg-emerald-700 text-white px-6 py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors"
            >
              Kontakt os
            </Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-gray-600">
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="md:hidden bg-[#ecfdf5] border-t border-gray-100 absolute w-full shadow-xl">
          <div className="px-4 py-6 space-y-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className="block text-lg font-medium text-gray-900 border-b border-gray-50 pb-2"
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center bg-emerald-700 text-white py-3 font-bold mt-4"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const PrivacyPolicy = () => (
  <div className="py-12 md:py-16 bg-[#ecfdf5]">
    <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-8 tracking-tighter uppercase">Privatlivspolitik</h1>
      <div className="prose prose-slate max-w-none space-y-8 text-gray-600 leading-relaxed">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduktion</h2>
          <p>
            Hos Alssund Rengøring værner vi om dit privatliv. Denne privatlivspolitik forklarer, hvordan vi indsamler, bruger og beskytter dine personoplysninger, når du besøger vores hjemmeside eller benytter vores tjenester.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Data vi indsamler</h2>
          <p>Vi indsamler oplysninger, som du selv giver os via vores kontaktformular, herunder:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Navn og kontaktinformation (email, telefonnummer)</li>
            <li>Information om den ønskede service</li>
            <li>Eventuelle beskeder eller specifikke ønsker til opgaven</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Formålet med indsamlingen</h2>
          <p>Dine oplysninger bliver udelukkende brugt til at:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Besvare dine henvendelser og give uforpligtende tilbud</li>
            <li>Levere de bestilte rengørings- eller malerydelser</li>
            <li>Overholde gældende lovgivning, herunder bogføringsloven</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Opbevaring og sikkerhed</h2>
          <p>
            Vi opbevarer dine data sikkert og beskyttet mod uautoriseret adgang. Vi gemmer kun dine oplysninger, så længe det er nødvendigt for at opfylde de formål, de blev indsamlet til, eller for at overholde lovkrav.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Dine rettigheder</h2>
          <p>
            Du har til enhver tid ret til at få indsigt i, hvilke personoplysninger vi behandler om dig. Du kan også anmode om at få rettet fejlagtige oplysninger eller få slettet dine data helt, såfremt vi ikke er lovmæssigt forpligtet til at gemme dem.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Kontakt</h2>
          <p>
            Hvis du har spørgsmål til vores behandling af dine personoplysninger, er du velkommen til at kontakte os på kontakt@alssundrengoring.dk.
          </p>
        </section>
      </div>
    </div>
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const Footer = () => (
  <footer className="bg-[#d0efdf] text-gray-900 py-12 relative overflow-hidden">
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-1">
          <div className="h-20 flex items-center">
            <Link to="/" className="inline-block h-full">
              <img 
                src="/Kundebilleder/alss_logo.png" 
                alt="Alssund Rengøring & Malerservice" 
                className="h-full w-auto object-contain"
              />
            </Link>
          </div>
          <p className="mt-6 text-gray-600 text-sm leading-relaxed">
            Professionel rengøring og malerservice i Alssund og omegn. Vi går op i detaljen og dit indeklima.
          </p>
        </div>
        <div>
          <h4 className="font-bold mb-6 text-emerald-700 uppercase tracking-widest text-xs">Services</h4>
          <ul className="space-y-3 text-gray-600 text-sm">
            {services.map(s => (
              <li key={s.id}>
                <Link to={`/services#${s.id}`} className="hover:text-emerald-700 transition-colors">
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/malerservice" className="hover:text-emerald-700 transition-colors">
                Malerservice
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold mb-6 text-emerald-700 uppercase tracking-widest text-xs">Virksomhed</h4>
          <ul className="space-y-3 text-gray-600 text-sm">
            <li><Link to="/" className="hover:text-emerald-700 transition-colors">Forside</Link></li>
            <li><Link to="/om-os" className="hover:text-emerald-700 transition-colors">Om os</Link></li>
            <li><Link to="/kontakt" className="hover:text-emerald-700 transition-colors">Kontakt</Link></li>
            <li><Link to="/privatlivspolitik" className="hover:text-emerald-700 transition-colors">Privatlivspolitik</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-bold mb-6 text-emerald-700 uppercase tracking-widest text-xs">Kontakt</h4>
          <ul className="space-y-3 text-gray-600 text-sm">
            <li className="flex items-center gap-2"><Phone size={14} /> +45 25 28 64 00</li>
            <li className="flex items-center gap-2"><Mail size={14} /> kontakt@alssundrengoring.dk</li>
            <li className="flex items-center gap-2"><MapPin size={14} /> 6400 Sønderborg</li>
          </ul>
        </div>
      </div>
      <div className="mt-12 pt-6 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 text-gray-500 text-xs">
        <p>© 2026 Alssund Rengøring. Alle rettigheder forbeholdes.</p>
        <div className="flex gap-6">
          <Facebook size={18} className="hover:text-emerald-700 cursor-pointer" />
          <Instagram size={18} className="hover:text-emerald-700 cursor-pointer" />
        </div>
      </div>
    </div>
  </footer>
);

const Home = () => (
  <>
    {/* Hero Section */}
    <section className="relative min-h-[85vh] flex items-center w-full bg-[#ecfdf5] py-16 px-4 sm:px-6 md:py-24 md:px-12 lg:px-24">
      <div className="container mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        
        {/* Left Column: Copy Content */}
        <div className="lg:col-span-5 flex flex-col items-start gap-6 max-w-xl">
          <span className="text-xs md:text-sm font-bold uppercase tracking-widest text-[#007a48]">
            Din lokale partner i rengøring og malerservice
          </span>
          
          <h1 className="text-4xl md:text-5xl lg:text-[54px] font-extrabold tracking-tight leading-[1.1] text-[#0c1c15]">
            Vi skaber flotte rammer
            <span className="block mt-2 italic font-semibold text-[#007a48]">
              så du kan fokusere på det vigtige
            </span>
          </h1>
          
          <p className="text-sm md:text-base text-[#536b61] leading-relaxed mt-2">
            Alssund Rengøring leverer privat rengøring, erhvervsrengøring og malerservice. Vi går op i kvalitet, stabilitet og et flot resultat hver gang.
          </p>
          
          <Link 
            to="/kontakt" 
            className="mt-4 inline-flex items-center gap-3 bg-[#007a48] hover:bg-[#0c1c15] text-white px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-200 ease-in-out"
          >
            Bestil et gratis besøg
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>

        {/* Right Column: Visual Showcase */}
        <div className="lg:col-span-7 w-full">
          <div className="relative shadow-[0_20px_50px_rgba(12,28,21,0.08)] aspect-square md:aspect-[4/3] lg:aspect-[1.2/1] overflow-hidden w-full bg-neutral-100">
            {/* Main Hero Image */}
            <img 
              src="/images/rent-hjem.jpg" 
              alt="Rent moderne hjem" 
              className="w-full h-full object-cover"
            />
            
            {/* Trust Badge Overlay */}
            <div className="absolute top-6 right-6 md:top-8 md:right-8 bg-[#007044] text-white p-6 md:p-8 max-w-[240px] md:max-w-[280px] shadow-lg flex flex-col gap-2">
              <span className="text-4xl md:text-5xl font-extrabold tracking-tight">100%</span>
              <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest leading-normal">
                Tilfredshedsgaranti på alle opgaver
              </span>
            </div>
          </div>
        </div>

      </div>
    </section>

    {/* Service Quick Grid */}
    <section className="py-12 md:py-16 bg-[#d0efdf] relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4 uppercase">Vores ydelser</h2>
            <p className="text-gray-500 text-base md:text-lg">Vi tilbyder rengøring og malerservice til både private og virksomheder.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto">
          {mainCategories.map((category) => (
            <div key={category.id} className="bg-white p-6 md:p-8 border border-slate-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-emerald-900/10 hover:-translate-y-2 transition-all duration-300 group flex flex-col h-full">
              <Link to={category.href} className="mb-6 overflow-hidden h-48 sm:h-64 block">
                <img 
                  src={category.image} 
                  alt={category.title} 
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${category.id === 'maler' ? 'object-top' : ''}`} 
                />
              </Link>
              <Link to={category.href}>
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors">
                  {category.title}
                </h3>
              </Link>
              <p className="text-slate-500 mb-6 leading-relaxed text-sm flex-grow">{category.description}</p>
              <Link to={category.href} className="text-emerald-700 font-bold text-sm flex items-center gap-2 group/link">
                Læs mere <ArrowRight size={16} className="group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* About Us Preview Section */}
    <FadeInSection>
    <section className="py-12 md:py-16 bg-[#ecfdf5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 order-2 lg:order-1 w-full">
            <img 
              src="privat.jpg" 
              alt="Alssund Rengøring Team" 
              className="w-full h-64 sm:h-80 md:h-[400px] lg:h-[500px] object-cover shadow-2xl"
            />
          </div>
          <div className="flex-1 order-1 lg:order-2 w-full">
            <span className="text-emerald-600 font-bold text-sm uppercase tracking-widest mb-4 block">Derfor skal du vælge os</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 md:mb-8 tracking-tighter uppercase break-words">Grundighed, stabilitet og godt håndværk</h2>
            <div className="space-y-6 text-gray-500 text-base md:text-lg leading-relaxed">
              <p>
                Hos Alssund Rengøring får du en løsning, hvor kvalitet og ordentlighed er i fokus. Vi arbejder grundigt, holder det, vi lover, og leverer et resultat, der er udført ordentligt fra start til slut.
              </p>
            </div>
            <div className="mt-8 md:mt-10">
              <Link to="/om-os" className="inline-flex items-center gap-2 bg-emerald-700 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium hover:bg-emerald-800 transition-all">
                Læs mere om os <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
    </FadeInSection>

    {/* CTA Section */}
    <FadeInSection>
    <section className="py-16 md:py-20 text-white overflow-hidden relative">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="hero-bg.jpg" 
          alt="Baggrund" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-emerald-900/90 mix-blend-multiply"></div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 className="text-3xl md:text-5xl font-bold mb-6 md:mb-8">Klar til et renere indeklima?</h2>
        <p className="text-emerald-50 text-base md:text-xl max-w-2xl mx-auto mb-10 md:mb-12">
          Udfyld vores kontaktformular og lad os give dig et uforpligtende tilbud på din næste rengøringsopgave eller malerprojekt.
        </p>
        <Link to="/kontakt" className="bg-white text-emerald-900 px-8 md:px-10 py-4 md:py-5 text-lg md:text-xl font-bold hover:bg-emerald-50 transition-colors inline-block shadow-xl">
          Få dit tilbud nu
        </Link>
      </div>

      {/* Logo element overlay */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center opacity-10 pointer-events-none z-5">
        <img 
          src="/Kundebilleder/alss_logo.png" 
          alt="" 
          className="w-full max-w-xl object-contain brightness-0 invert"
        />
      </div>
    </section>
    </FadeInSection>
  </>
);

const Services = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);

  return (
    <div className="py-12 md:py-16 relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 uppercase tracking-tighter">Rengøring</h1>
        <p className="text-gray-500 text-lg md:text-xl max-w-2xl mb-12">Vi tilbyder et bredt udvalg af professionelle løsninger til både private og erhverv.</p>
        
        <div className="grid grid-cols-1 gap-12 md:gap-20">
          {services.map((service, idx) => (
            <div key={service.id} id={service.id} className="scroll-mt-32">
              <FadeInSection>
                <div className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-16 items-center`}>
                  <div className="w-full lg:flex-1">
                    <img src={service.image} alt={service.title} className="w-full h-64 sm:h-80 md:h-[400px] lg:h-[500px] object-cover shadow-lg" />
                  </div>
                  <div className="w-full lg:flex-1">
                    <span className="text-emerald-600 font-bold text-sm uppercase tracking-widest mb-3 block">
                      {service.id === 'erhverv' ? 'Til virksomheder' : service.id === 'privat' ? 'Til private' : service.id === 'sommerhus' ? 'Sommerhuse' : service.id === 'haandvaerker' ? 'Efter byggeri og renovering' : service.id === 'fliserens' ? 'Udendørs arealer' : 'Vores ekspertise'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{service.id === 'fliserens' ? 'Fliserens' : service.title}</h2>
                    <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-8">
                      {service.id === 'erhverv' 
                        ? 'Vi hjælper virksomheder med professionel rengøring, der skaber rene, velholdte og præsentable rammer for både medarbejdere og kunder. Vi tilpasser løsningen til jeres hverdag, så I får en stabil service med fokus på kvalitet og grundighed.' 
                        : service.id === 'privat'
                        ? 'Vi hjælper dig med privat rengøring, så du får mere ro og overskud i hverdagen. Hos os får du en grundig og stabil service, hvor vi passer godt på dit hjem og leverer et resultat, der er udført ordentligt fra start til slut.'
                        : service.id === 'sommerhus'
                        ? 'Vi tilbyder sommerhusrengøring for både ejere, udlejere og bureauer. Med fokus på grundighed, stabile aftaler og et flot resultat sørger vi for, at boligen altid fremstår ren, præsentabel og klar til næste ophold.'
                        : service.id === 'haandvaerker'
                        ? 'Vi tilbyder håndværkerrengøring efter nybyg, renovering og ombygning. Vi rengør grundigt efter byggearbejdet og sørger for, at boligen eller lokalet står rent, præsentabelt og klar til brug.'
                        : service.id === 'fliserens'
                        ? 'Vi tilbyder fliserens, der fjerner snavs, belægninger og misfarvninger, så dine udendørs arealer fremstår rene, velholdte og indbydende. Vi sørger for et grundigt resultat, der giver dine fliser nyt liv.'
                        : service.description}
                    </p>
                    <ul className="space-y-3 mb-10">
                      {(service.id === 'erhverv' 
                        ? ['Rengøring tilpasset jeres arbejdstider', 'Stabile aftaler og fast kvalitet', 'Præsentable rammer hver dag']
                        : service.id === 'privat'
                        ? ['Rengøring tilpasset din hverdag', 'Grundig og stabil service', 'Et rent og indbydende hjem']
                        : service.id === 'sommerhus'
                        ? ['Klar til næste gæst', 'Rengøring ved hvert skift', 'Rent og indbydende sommerhus']
                        : service.id === 'haandvaerker'
                        ? ['Grundig rengøring efter byggearbejde', 'Klar til indflytning eller brug', 'Rent og præsentabelt resultat']
                        : service.id === 'fliserens'
                        ? ['Fjernelse af alger og belægninger', 'Flottere og mere velholdte fliser', 'Et rent og indbydende udtryk']
                        : ['Miljøvenlige midler', 'Fast personale', 'Forsikret arbejde']
                      ).map(item => (
                        <li key={item} className="flex items-start gap-3 text-gray-700 font-medium text-sm md:text-base">
                          <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={18} /> {item}
                        </li>
                      ))}
                    </ul>
                    <Link to="/kontakt" className="inline-flex items-center gap-2 border-b-2 border-emerald-600 pb-1 text-emerald-700 font-bold hover:gap-4 transition-all text-sm md:text-base">
                      Book {service.title.toLowerCase()} <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </FadeInSection>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    try {
      await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Submission failed", error);
    }
  };

  if (submitted) {
    return (
      <div className="py-32 text-center container mx-auto max-w-7xl px-4">
        <CheckCircle2 size={80} className="text-emerald-600 mx-auto mb-8" />
        <h1 className="text-4xl font-bold mb-4">Tak for din besked!</h1>
        <p className="text-gray-500 text-lg">Vi vender tilbage til dig hurtigst muligt, typisk inden for 24 timer.</p>
        <Link to="/" className="mt-12 inline-block bg-gray-900 text-white px-8 py-3">Tilbage til forsiden</Link>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 bg-[#ecfdf5]">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 md:mb-8 tracking-tighter uppercase">Kontakt os</h1>
            <p className="text-gray-500 text-base md:text-lg mb-10 md:mb-12">Har du spørgsmål eller ønsker du et uforpligtende tilbud? Vi sidder klar til at hjælpe dig med din næste opgave.</p>
            
            <div className="space-y-6 md:space-y-8">
              <div className="flex gap-4 md:gap-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#d0efdf] flex items-center justify-center text-emerald-700 shrink-0">
                  <Phone size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm md:text-base">Ring til os</h4>
                  <p className="text-gray-500 text-sm md:text-base">+45 25 28 64 00</p>
                </div>
              </div>
              <div className="flex gap-4 md:gap-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#d0efdf] flex items-center justify-center text-emerald-700 shrink-0">
                  <Mail size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm md:text-base">Send en mail</h4>
                  <p className="text-gray-500 text-sm md:text-base">kontakt@alssundrengoring.dk</p>
                </div>
              </div>
            </div>
          </div>
          
          <FadeInSection>
          <div className="bg-[#d0efdf] p-6 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="sr-only absolute">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">Navn</label>
                <input required type="text" name="name" className="w-full border-b-2 border-gray-200 bg-transparent py-2 md:py-3 focus:border-emerald-600 outline-none transition-colors text-sm md:text-base" placeholder="Dit navn" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">Email</label>
                <input required type="email" name="email" className="w-full border-b-2 border-gray-200 bg-transparent py-2 md:py-3 focus:border-emerald-600 outline-none transition-colors text-sm md:text-base" placeholder="din@email.dk" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">Tjeneste</label>
                <select name="service" className="w-full border-b-2 border-gray-200 bg-transparent py-2 md:py-3 focus:border-emerald-600 outline-none transition-colors text-sm md:text-base">
                  {contactServices.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">Besked</label>
                <textarea required name="message" rows="4" className="w-full border-b-2 border-gray-200 bg-transparent py-2 md:py-3 focus:border-emerald-600 outline-none transition-colors text-sm md:text-base" placeholder="Fortæl os om din opgave"></textarea>
              </div>
              
              <button type="submit" className="w-full bg-emerald-700 text-white font-bold py-4 md:py-5 hover:bg-emerald-800 transition-colors uppercase tracking-widest text-sm md:text-base">
                Send besked
              </button>
            </form>
          </div>
          </FadeInSection>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-[#ecfdf5] font-sans text-gray-900">
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/malerservice" element={<Malerservice />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/privatlivspolitik" element={<PrivacyPolicy />} />
          <Route path="/om-os" element={<About />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
